import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useAuth } from '../auth';
import { useI18n, isLocale } from '../i18n';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { useGuestMode } from '../hooks/useGuestMode';
import { track } from '../lib/analytics';
import './AuthPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function AuthPage() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('reset_token');
  const verifyToken = searchParams.get('verify_token');
  const location = useLocation();
  // RequireAuthOrGuest yönlendirmesinde geldiği sayfa (örn. /room/xyz)
  const from = (location.state as { from?: string } | null)?.from || '/';

  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'reset'>(
    resetToken ? 'reset' : 'login'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState<'success' | 'failed' | 'network_error' | null>(null);

  const { login, register, loginWithGoogle, loginWithApple } = useAuth();
  const { enterGuest } = useGuestMode();
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');
  const { t, setLocale } = useI18n();

  // Misafir olarak devam: guest modunu aç, geldiği sayfaya (veya ana sayfaya) dön
  const handleGuestContinue = () => {
    track('cta_click', { placement: 'auth', action: 'guest' });
    enterGuest();
    navigate(from, { replace: true });
  };

  // reset_token param geldiğinde mode'u ayarla
  useEffect(() => {
    if (resetToken) setMode('reset');
  }, [resetToken]);

  // E-posta linkindeki lang param'ı sayfa dilini e-postayla eşitler
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang && isLocale(lang)) setLocale(lang);
  }, [searchParams, setLocale]);

  // verify_token param geldiğinde otomatik dogrula.
  // Çevrilmiş metin değil, sadece durum yazılır — gösterilecek mesaj render
  // anında güncel `t`'den türetilir (bkz. displayError/displaySuccess).
  useEffect(() => {
    if (!verifyToken) return;
    setLoading(true);
    fetch(`${API}/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: verifyToken }),
    })
      .then(res => setVerifyStatus(res.ok ? 'success' : 'failed'))
      .catch(() => setVerifyStatus('network_error'))
      .finally(() => setLoading(false));
  }, [verifyToken]);

  const displaySuccess = success || (verifyStatus === 'success' ? t.auth.verifyEmailSuccess : '');
  const displayError = error || (
    verifyStatus === 'failed' ? t.auth.verifyEmailFailed
    : verifyStatus === 'network_error' ? t.auth.verifyEmailError
    : ''
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        navigate(from);
      } else if (mode === 'register') {
        await register(email, password, nickname || t.auth.nicknamePlaceholder, referralCode.trim() || undefined);
        try { localStorage.removeItem('seen_tutorial'); } catch {}
        navigate(from);
      } else if (mode === 'forgot') {
        const res = await fetch(`${API}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (!res.ok) throw new Error(t.common.error);
        setSuccess(t.auth.forgotPasswordSent);
      } else if (mode === 'reset') {
        if (!newPassword || newPassword.length < 6) {
          throw new Error(t.profile.pwTooShort);
        }
        const res = await fetch(`${API}/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken, new_password: newPassword }),
        });
        if (!res.ok) {
          throw new Error(t.auth.resetInvalidToken);
        }
        setSuccess(t.auth.resetSuccess);
        setTimeout(() => navigate('/auth'), 2000);
      }
    } catch (err: any) {
      const msg: string = err.message || t.common.error;
      if (mode === 'login' && msg.toLowerCase().includes('fetch')) {
        setMode('register');
        setError(t.auth.noAccountSuggestion);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
      navigate(from);
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message || t.common.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithApple();
      navigate(from);
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message || t.common.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: typeof mode) => {
    if (next === 'register' && mode !== 'register') {
      track('cta_click', { placement: 'auth', action: 'register_form' });
    }
    setMode(next);
    setError('');
    setSuccess('');
  };

  return (
    <div className="auth-page">
      <SEO path="/auth" />
      <AppHeader onBack={smartBack} backLabel={t.common.backToHome} />
      <div className="auth-card">
        <h1>
          {mode === 'login' ? t.auth.loginTitle
            : mode === 'register' ? t.auth.registerTitle
            : mode === 'forgot' ? t.auth.forgotPasswordTitle
            : t.auth.resetPasswordTitle}
        </h1>
        <p className="auth-subtitle">
          {mode === 'login' ? t.auth.loginSubtitle
            : mode === 'register' ? t.auth.registerSubtitle
            : mode === 'forgot' ? t.auth.forgotPasswordDesc
            : ''}
        </p>

        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <div className="auth-field">
              <label>{t.auth.nickname}</label>
              <input
                type="text"
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                placeholder={t.auth.nicknamePlaceholder}
                maxLength={50}
              />
            </div>
          )}

          {mode === 'register' && (
            <div className="auth-field">
              <label>{t.auth.referralCodeLabel}</label>
              <input
                type="text"
                value={referralCode}
                onChange={e => setReferralCode(e.target.value.toUpperCase())}
                placeholder={t.auth.referralCodePlaceholder}
                maxLength={8}
              />
            </div>
          )}

          {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
            <div className="auth-field">
              <label>{t.auth.email}</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t.auth.emailPlaceholder}
                required
              />
            </div>
          )}

          {(mode === 'login' || mode === 'register') && (
            <div className="auth-field">
              <label>{t.auth.password}</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t.auth.passwordPlaceholder}
                minLength={6}
                required
              />
            </div>
          )}

          {mode === 'reset' && (
            <div className="auth-field">
              <label>{t.auth.newPassword}</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder={t.auth.newPasswordPlaceholder}
                minLength={6}
                required
              />
            </div>
          )}

          {displayError && <div className="auth-error">{displayError}</div>}
          {displaySuccess && <div className="auth-success">{displaySuccess}</div>}

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? t.common.loading
              : mode === 'login' ? t.auth.loginBtn
              : mode === 'register' ? t.auth.registerBtn
              : mode === 'forgot' ? t.auth.forgotPasswordBtn
              : t.auth.resetPasswordBtn}
          </button>
        </form>

        {/* Alt linkler */}
        <div className="auth-switch">
          {mode === 'login' && (
            <>
              <p>
                {t.auth.noAccount}{' '}
                <button type="button" onClick={() => switchMode('register')}>
                  {t.auth.switchToRegister}
                </button>
              </p>
              <p>
                <button type="button" className="auth-link-subtle" onClick={() => switchMode('forgot')}>
                  {t.auth.forgotPassword}
                </button>
              </p>
            </>
          )}
          {mode === 'register' && (
            <p>
              {t.auth.hasAccount}{' '}
              <button type="button" onClick={() => switchMode('login')}>
                {t.auth.switchToLogin}
              </button>
            </p>
          )}
          {mode === 'forgot' && (
            <p>
              <button type="button" onClick={() => switchMode('login')}>
                {t.auth.switchToLogin}
              </button>
            </p>
          )}
          {mode === 'reset' && !success && (
            <p>
              <button type="button" onClick={() => navigate('/auth')}>
                {t.auth.switchToLogin}
              </button>
            </p>
          )}
        </div>

        {(mode === 'login' || mode === 'register') && (
          <button
            type="button"
            className="auth-skip"
            onClick={handleGuestContinue}
          >
            {t.auth.continueAnonymous}
          </button>
        )}

        {(mode === 'login' || mode === 'register') && (
          <div className="auth-social">
            <div className="auth-divider"><span>veya</span></div>
            <button
              type="button"
              className="auth-social-btn auth-google-btn"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {t.auth.continueWithGoogle}
            </button>
            <button
              type="button"
              className="auth-social-btn auth-apple-btn"
              onClick={handleAppleLogin}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M16.36 12.78c.02 2.5 2.19 3.33 2.22 3.34-.02.06-.35 1.19-1.14 2.36-.69 1.01-1.4 2.02-2.52 2.04-1.1.02-1.45-.65-2.71-.65-1.25 0-1.64.63-2.68.67-1.08.04-1.9-1.1-2.6-2.11-1.42-2.05-2.51-5.8-1.05-8.33.72-1.26 2.02-2.06 3.42-2.08 1.06-.02 2.07.71 2.71.71.65 0 1.87-.88 3.15-.75.54.02 2.05.22 3.02 1.64-.08.05-1.8 1.05-1.79 3.14M14.28 4.6c.57-.69.95-1.65.85-2.6-.82.03-1.81.55-2.4 1.23-.53.61-.99 1.58-.86 2.51.91.07 1.84-.46 2.41-1.14"/>
              </svg>
              {t.auth.continueWithApple}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
